// Last saved salary check  ***********************************************************************
// pathname: /v1/resume-operation/resume/last-suggested-salary
// ************************************************************************************************
import { useSwrApi } from 'src/hooks/useSwrApi';

/**
 * @description SWR: the user's most recent salary check — the stored result reference
 * (`id`, `url`, `privateUrl`) plus the input they originally submitted. Not the statistics:
 * the full result lives behind `/v1/resume-operation/resume/suggested-salary/{id}`, which the
 * result page already fetches server-side.
 *
 * Endpoint is auth-only (401 for guests), so `shouldFetch` must be gated on the session or
 * every signed-out visitor generates a pointless failing request.
 *
 * @param locale - string. The locale to use for the request. Default: 'ro'.
 * @param shouldFetch - boolean. Skip the request when the user is not authenticated.
 *
 * @example
 * const { data: lastCheck } = useSwrLastSuggestedSalary('ro', isAuth);
 */
export const useSwrLastSuggestedSalary = (locale: string = 'ro', shouldFetch: boolean = true) => {
  const {
    data, error, isLoading, isValidating, mutate,
  } = useSwrApi<ApiSchema<'CvWorthRead'>>({
    url: '/v1/resume-operation/resume/last-suggested-salary',
    shouldFetch,
    locale,
    apiOptions: {
      // The stored result only changes when the user runs a new check, and doing so
      // navigates away from this page — so there is nothing to revalidate on focus.
      revalidateOnFocus: false,
      dedupingInterval: 1000 * 60 * 5, // 5 minutes
    },
  });

  return {
    data,
    error,
    mutate,
    isLoading,
    isValidating,
  };
};
